import { getAllData } from "apis/customApiHandler";
import Spinner from "components/Spinner";
import useDebounce from "hooks/useDebounce";
import Model from "components/Modal";
import { Form, FormikProvider, useFormik } from "formik";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useQuery, useQueryClient } from "react-query";
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from "react-redux";
import { Button } from "styled";
import { TD, TH } from "styled/table";
import ReactSelect from "react-select";
import { BiTransfer } from "react-icons/bi";
import { Input } from "styled";
import { createDocument } from "apis/customApiHandler";
import checkPermission from "utils/checkPermission";
import permissions from "utils/permissionConstants";
import { EKS } from "aws-sdk";
import InputField from "components/formik/InputField";
import Switch from '@mui/material/Switch';
import { teal } from "@mui/material/colors";
import { alpha, styled } from '@mui/material/styles';
import { AsyncPaginate } from "react-select-async-paginate";


const StockTransfer = () => {
  const qClient = useQueryClient();
  const asyncPaginateRef = useRef(null);
  const [search, setSearch] = useState("");
  const [tyres, setTyres] = useState("");
  const [isLoading, setIsLoading] = useState("");
  const [gettingTyres, setGettingTyres] = useState(true)
  const [disabled, setDisabled] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0);
  const [contractType, setContractType] = useState("CPKM")
  const navigate = useNavigate();


  const formik = useFormik({
    initialValues: {
      fromFleet: "",
      fromHub: "",
      status: "",
      toFleet: "",
      toHub: "",
      product: "",
      stencilNumbers: [],
      tyres: [],
      contractType: "",
      type: "INTERNAL",
      reason: "",
    },
    onSubmit: async (values) => {
      setDisabled(true)
      try {
        let tyres = [...values.tyres?.map(tyre => {
          const NDP = parseFloat(tyre.NDP).toFixed(2);
          const amount = tyre.originalNSD ? parseFloat((NDP/tyre.originalNSD)*(tyre.averageNSD ?? tyre.originalNSD)).toFixed(2): NDP;
          const averageNSD = tyre.averageNSD ?? tyre.originalNSD
          return {
            ...tyre, 
            amount: amount,
            discount: 0,
            afterDiscount: amount,
            averageNSD: averageNSD,
            blockedForStockTransfer: true,
          }
        })];

        if(values.type === "EXTERNAL"){
          delete values.stencilNumbers;
        }else{
          delete values.tyres;
        }
        const data = contractType === "NON CPKM" ||  (values.type === "INTERNAL" &&  getFleetOptions().find(fleet => fleet.value === formik.values.toFleet))?.KAMs?.includes(currentUser._id) ? await createDocument("tyre/stockTransfer", {...values, fromKAM: true, transferReason: values.reason, transferedBy: currentUser._id, transferedAt: new Date(new Date() - (330 * 60 * 1000)), tyres, contractType, status: "TRANSFERED"}):
        await createDocument("tyre/createStockTransferRequest", {
          ...values,
          tyres: tyres,
          contractType,
          ...contractType === "NON CPKM" ? {status: "TRANSFERED"} : {status: "REQUESTED"},
        });
        if (data) {
          toast.success(data.message);
          navigate("/stock-transfer")
          handleRefresh();
          // formik.setValues({})
        }
      } catch (error) {
        // console.log({error})
        toast.error(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Something went wrong"
        );
      } finally{
        setDisabled(false)
      }
    },

    validate: (values) => {
      let errors = {};
      // console.log(values.type, values.toFleet, values.fromFleet)
      if (!values.fromFleet) errors.fromFleet = "Required";
      if (!values.fromHub) errors.fromHub = "Required";
      if (!values.toFleet) errors.toFleet = "Required";
      if (!values.toHub) errors.toHub = "Required";
      if (values.toFleet && (values.toFleet === values.fromFleet) && values.type === "EXTERNAL") errors.toFleet = "Cannot be same as existing fleet";
      if (values.toHub && (values.toHub === values.fromHub)) errors.toHub = "Cannot be same as existing hub";
      // if(values.toHub && values.type === "INTERNAL") if (!(values.stencilNumbers.length)) errors.tyres = "Please select Tyres for internal stock transfer";
      // if(values.toHub && values.type === "EXTERNAL"){
        if(!(values.tyres?.length)) errors.tyres = "Please select Tyres for external stock transfer";
      // }
      if (!values.reason) errors.reason = "Required";
      // console.log({errors})
      setDisabled(errors && Object.keys(errors)?.length)
      return errors;
    },
  });

  const { data: allFleets } = useQuery([`/customer-fleet`], () =>
    getAllData("/customer-fleet", {
      limit: 3000,
    })
  );
  const { data: allHubs } = useQuery([`/customer-hub`], () =>
    getAllData("/customer-hub", {
      limit: 3000,
    })
  );

  const { data: allFleetsTo } = useQuery([`/customer-fleet-to`], () =>
    getAllData("/customer-fleet", {
      limit: 3000,
      fromUsers: 1,
    })
  );
  const { data: allHubsTo } = useQuery([`/customer-hub-to`], () =>
    getAllData("/customer-hub", {
      limit: 3000,
      ignoreAssignedHubs: 1,
      fromUsers: 1,
    })
  );
  // console.log({asyncPaginateRef})

  const handleRefresh = () => {
    // Increment the key to force a remount
    setRefreshKey(prevKey => prevKey + 1);
  };


  const getFleetOptions = useCallback(() => {
    if (allFleets && allFleets.data && allFleets.data.length) {
      return [
        ...allFleets?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
            mappedFleets: fleet.mappedFleets,
            KAMs: fleet.KAMs,
          };
        }),
      ];
    }
  }, [allFleets]);

  const getToFleetOptions = useCallback(() => {
    if (allFleetsTo && allFleetsTo.data && allFleetsTo.data.length) {
      return [
        ...allFleetsTo?.data?.map((fleet) => {
          return {
            value: fleet._id,
            label: fleet.fleetName,
            mappedFleets: fleet.mappedFleets,
            KAMs: fleet.KAMs,
          };
        }),
      ];
    }
  }, [allFleetsTo]);

  const getHubOptions = useCallback(() => {
    if (allHubs && allHubs.data && allHubs.data.length) {
      const selectedFleet = allFleets?.data?.find((fleet) => {
        return fleet._id === formik.values.fromFleet;
      });
      return [
        ...allHubs?.data
          ?.filter((hub) => hub.fleetCode === selectedFleet?.fleetCode)
          .map((hub) => {
            return {
              value: hub._id,
              label: hub.hubName,
            };
          }),
      ];
    }
  }, [allHubs, allFleets, formik.values.fromFleet]);

  const getToHubOptions = useCallback(() => {
    if (allHubsTo && allHubsTo.data && allHubsTo.data.length) {
      const selectedFleet = allFleetsTo?.data?.find((fleet) => {
        return fleet._id === formik.values.toFleet;
      });
      return [
        ...allHubsTo?.data
          ?.filter((hub) => hub.fleetCode === selectedFleet?.fleetCode)
          .map((hub) => {
            return {
              value: hub._id,
              label: hub.hubName,
            };
          }),
      ];
    }
  }, [allHubsTo, allFleetsTo, formik.values.toFleet]);

  let currentUser = useSelector((state) => state.user.currentUser);

  const label = { inputProps: { 'aria-label': 'Switch demo' } };

  const GreenSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: teal[400],
      '&:hover': {
        backgroundColor: alpha(teal[400], theme.palette.action.hoverOpacity),
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: teal[400],
    },
  }));

  const loadOptions = useCallback(async (searchQuery, loadedOptions, { page }) => {
    if(formik.values.fromHub){
      // console.log("here");
    const tempGettingTyres = gettingTyres;
    const response = await getAllData("/tyre/availableStocksByHub", {
      page: page,
      hub: formik.values.fromHub,
      fleet: formik.values.fromFleet,
      limit: 10,
      searchQuery: searchQuery.toUpperCase(),
      blockedForStockTransfer: false,
      // ...formik.values.type==="EXTERNAL" ? {contractType: "CPKM"}: {contractType}
      contractType
    });
    setTyres([...loadedOptions, ...response.data])
    // setGettingTyres(false)
    // console.log(response.paginate)
      return {
        options: response.data,
        hasMore: response.data?.length
          ? response.paginate.currentPage !== response.paginate.totalPage
          : false,
        additional: {
            page: page + 1,
          },
      };
    }
  
  }, [formik.values.fromHub, contractType]);



  useEffect(()=>{
    formik.setFieldValue("stencilNumbers", "");
    formik.setFieldValue("tyres", [])
    setRefreshKey(key => key + 1)
  }, [formik.values.fromHub, formik.values.fromFleet]);
  const contractTypes = [
    { label: "CPKM", value: "CPKM" },
    { label: "NON CPKM", value: "NON CPKM" },
  ];


  // console.log("errors", formik.errors)

  return (
    <div>
      <div className="mx-10 my-2">
        <div className="mt-5 flex justify-start gap-6 items-center grid grid-cols-1 md:grid-cols-2 flex-wrap lg:flex-nowrap font-medium">
          <label className=" w-full ">
            Select Fleet (From)
            <ReactSelect
              options={getFleetOptions()}
              value={{
                value: formik.values.fromFleet,
                label: getFleetOptions()?.find(
                  (fleet) => fleet.value === formik.values.fromFleet
                )?.label,
              }}
              onChange={(value) => {
                formik.setFieldValue("type", "INTERNAL");
                formik.setFieldValue("fromFleet", value.value);
                // if (formik.values.type === "INTERNAL")
                formik.setFieldValue("toFleet", value.value);
              }}
              name="fromFleet"
              placeholder="Select Fleet"
            />
            {formik.touched.fromFleet && formik.errors.fromFleet ? (
              <p className="text-red-500 font-normal">
                *{formik.errors.fromFleet}
              </p>
            ) : null}
          </label>
          <label className=" w-full ">
            Select Hub (From)
            <ReactSelect
              options={getHubOptions()}
              value={{
                value: formik.values.fromHub,
                label: getHubOptions()?.find(
                  (hub) => hub.value === formik.values.fromHub
                )?.label,
              }}
              onChange={(value) => {
                formik.setFieldValue("fromHub", value.value);
                formik.setFieldValue("tyres", []);
                formik.setFieldValue("stencilNumbers", []);
                setGettingTyres(true);
              }}
              name="fromHub"
              placeholder="Select Hub"
            />
            {formik.touched.fromHub && formik.errors.fromHub ? (
              <p className="text-red-500 font-normal">
                *{formik.errors.fromHub}
              </p>
            ) : null}
          </label>
          <label className="w-full ">
            Select Fleet (To)
            <ReactSelect
              options={getToFleetOptions()}
              // isDisabled = {formik.values.type==="INTERNAL"}
              isDisabled={
                !checkPermission(
                  [permissions.external_stock_transfer],
                  currentUser
                )
              }
              value={{
                value: formik.values.toFleet,
                label: getToFleetOptions()?.find(
                  (fleet) => fleet.value === formik.values.toFleet
                )?.label,
                mappedFleets: getToFleetOptions()?.find(
                  (fleet) => fleet.value === formik.values.toFleet
                )?.mappedFleets,
                }}
              onChange={(value) => {

                console.log(value, value.mappedFleets, formik.values.fromFleet)

                if (formik.values.fromFleet === value.value || value.mappedFleets?.includes(formik.values.fromFleet)) {
                  formik.setFieldValue("type", "INTERNAL");
                } else {
                  formik.setFieldValue("type", "EXTERNAL");
                }
                formik.setFieldValue("toFleet", value.value);
                // handleRefresh();
              }}
              name="toFleet"
              placeholder="Select Fleet"
            />
            {formik.errors && formik.errors.toFleet ? (
              <p className="text-red-500 font-normal">
                *{formik.errors.toFleet}
              </p>
            ) : null}
          </label>
          <label className="w-full ">
            Select Hub (To)
            <ReactSelect
              options={getToHubOptions()}
              value={{
                value: formik.values.hub,
                label: getToHubOptions()?.find(
                  (hub) => hub.value === formik.values.toHub
                )?.label,
              }}
              onChange={(value) => {
                formik.setFieldValue("toHub", value.value);
              }}
              name="toHub"
              placeholder="Select Hub"
            />
            {formik.errors.toHub ? (
              <p className="text-red-500 font-normal">*{formik.errors.toHub}</p>
            ) : null}
          </label>
        </div>

        <div className="mt-5 flex justify-start gap-6 items-center grid grid-cols-2 md:grid-cols-4 flex-wrap lg:flex-nowrap font-medium">
          <label className=" w-full col-span-1 ">
            Select Contract Type
            <ReactSelect
              // isDisabled = {formik.values.type === "EXTERNAL"}
              options={contractTypes}
              value={
                {value: contractType,
                label: contractTypes?.find((hub) => hub.value === contractType)
                  ?.label,}
              }
              onChange={(value) => {
                formik.setFieldValue("tyres", []);
                formik.setFieldValue("stencilNumbers", []);
                setContractType(value.value);
                setGettingTyres(true);
                handleRefresh();
              }}
              name="contractType"
              placeholder="Select Contract Type"
            />
            {formik.touched.fromHub && formik.errors.fromHub ? (
              <p className="text-red-500 font-normal">
                *{formik.errors.fromHub}
              </p>
            ) : null}
          </label>
          <label className="w-full col-span-3 ">
            Select Stencil Numbers
            <AsyncPaginate
              id="companyId"
              // ref={asyncPaginateRef}
              key={refreshKey}
              loadOptions={loadOptions}
              // value={formik.values.tyres}
              getOptionValue={(option) => option}
              getOptionLabel={(option) => option.stencilNo}
              onChange={async (values) => {
                // if (formik.values.type === "INTERNAL")
                //   formik.setFieldValue(
                //     "stencilNumbers",
                //     values.map((value) => value.stencilNo)
                //   );
                // else 
                formik.setFieldValue("tyres", values);
              }}
              additional={{
                page: 1,
              }}
              isSearchable={true}
              placeholder="Select an option"
              debounceTimeout={500}
              isMulti={true}
              cacheOptions={false}
            />
            {formik.errors.stencilNumbers || formik.errors.tyres ? (
              <p className="text-red-500 font-normal">*{formik.errors.tyres}</p>
            ) : null}
          </label>
        </div>
          {
            <label className="w-full flex flex-col col-span-4 mt-2 py-2 font-semibold">
              Enter Reason for Stock Transfer
              <textarea
                // disabled={formik.values.type !== "EXTERNAL"}
                className={` w-full rounded-md py-2 px-3 text-gray-500  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 border-1`}
                label="Reason"
                name="reason"
                maxLength={150}
                // key="reason"
                placeholder="Enter remarks max 150 chars"
                formik={formik}
                onChange={(e) => {
                  //console.log(e.target.value)
                  formik.setFieldValue("reason", e.target.value);
                }}
              ></textarea>
              {formik.errors.reason ? (
              <p className="text-red-500 font-normal">
                *{formik.errors.reason}
              </p>
            ) : null}
            </label>
          }
          {checkPermission(
            [
              permissions.manage_stencil_search,
              permissions.manage_stencil_menu,
              permissions.stock_transfer,
            ],
            currentUser
          ) ? (
            <Button
              type="button"
              className="float-right h-12 mb-4 justify-end col-span-1"
              bgPrimary
              disabled={disabled}
              onClick={() => {
                // setShowModal(false);
                formik.handleSubmit();
              }}
            >
              Save Changes
            </Button>
          ) : (
            <></>
          )}
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        // <></>
        <div className="flex flex-col w-full m-2 lg:m-10">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <TH scope="col">S.N.</TH>
                      <TH scope="col">Stencil Number</TH>
                      {/* <TH scope="col">Fleet</TH>
                      <TH scope="col">Hub</TH> */}
                      <TH scope="col">Status</TH>
                      <TH scope="col">Material</TH>
                      {/* <TH scope="col">Quantity</TH>
                      <TH scope="col">Action</TH> */}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tyres &&
                      tyres?.map((item, index) => (
                        <ViewList
                          data={item}
                          key={item?._id}
                          index={index}
                          formik={formik}
                          searchQuery={search}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ViewList = ({ data, index, formik, searchQuery }) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <Fragment>
      <tr key={data._id} className={`${index % 2 !== 0 && "bg-gray-100"}`}>
        <TD>{index + 1}</TD>
       
        <TD>{data?.stencilNo}</TD>
        {/* <TD>{data?.fleetName}</TD>
        <TD>{data?.hubName}</TD> */}
        <TD>{ data?.status === 'TRANSFERED' ? 'TRANSFERRED' : data?.status}</TD>
        <TD>
          {data.productName ? data.productName: '---'}
          {/* {data.product || data.productMaster
            ? data.product
              ? data?.product?.productName
              : data?.productMaster?.productName
            : "---"} */}
        </TD>
        {/* <TD>{data?.quantity}</TD> */}
        {/* <TD>
          <Button onClick={() => setShowModal(true)} bgPrimary>
            <BiTransfer />
          </Button>
        </TD> */}
      </tr>
      {/* {showModal && (
        <TransferModal
          data={{
            ...data,
            fromFleet: formik.values.fromFleet,
            fromHub: formik.values.fromHub,
          }}
          formik={formik}
          setShowModal={setShowModal}
          searchQuery={searchQuery}
        />
      )} */}
    </Fragment>
  );
};

export default StockTransfer;